import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { PaymentStatus } from "../features/pointOfSale/enums/PaymentStatus";
import { PaymentMethod } from "../features/pointOfSale/enums/PaymentMethod";
import { PaymentType } from "../features/pointOfSale/enums/PaymentType";
import { Payment } from "../features/pointOfSale/features/checkout/payment/models/Payment";
import { AccountParticipant } from "../features/pointOfSale/features/account/models/AccountParticipant";
import { v4 as uuidv4 } from "uuid";
import { InvoiceStatus } from "../features/pointOfSale/enums/InvoiceStatus";
import { PosTabsType } from "../features/pointOfSale/enums/PosTabsType";
import { Participant } from "../features/accounts/models/Participant";
import { PreviewApiStatus } from "../features/pointOfSale/enums/PreviewApiStatus";
import { CharityStatus } from "@unleashed-admin/features/pointOfSale/enums/CharityStatus";
import { Account } from "@unleashed-admin/features/pointOfSale/features/account/models/Account";
import { CharityType } from "@unleashed-admin/features/pointOfSale/enums/CharityType";
import {
  MembershipAllocationDataModel,
  CartItem,
  PrePurchasedItem,
  Promo,
  TransactionDetail,
  InvoiceSummary,
  CharityDetails,
  TicketUpgradeDataModel,
  BaseProduct,
  PurchaseViewModel,
  PurchaseItemViewModel,
  ProductOptionViewModel,
  Product,
} from "@unleashed-admin/features/pointOfSale/models";
import {
  updateStateWithSecondaryAccounts,
  assignMembershipToParticipant,
  unAssignMembershipFromParticipant,
  unassignAllParticipantsFromMembership,
  unAssignAllMemberships,
  updateStateForPrePurchasedItem,
  findSimilarProduct,
  updateProductQuantity,
  handleUpgradedTicketRemoval,
  removeOptionalModifierFromComboCartItem,
  isPaidRedeemItem,
  removeOptionalModifierFromRedeemingItem,
  removeOptionalModifierFromCartItem,
  resetCharityData,
  handleRedeemingItemReset,
  isParentTicket,
  getProductIdOrTicketIdFromProduct,
  updateProductDiscountForBooking,
  updateProductDiscountForItemInCart,
  updateProductDiscountForCombo,
  updateProductDiscountForBundle,
  isSameProduct,
  updateStateOnCheckIn,
  applyTicketUpgradeChanges,
  removeItemFromCart,
  handleUpgradeTicketsDiscounts,
  handleRedemptionDiscounts,
  isMembership,
} from "@unleashed-admin/features/pointOfSale/utils";
import { CreateAcknowledgement } from "@unleashed-admin/features/accounts/features/contracts/models/CreateAcknowledgement";
import { PointOfSaleCategory } from "@unleashed-admin/features/pointOfSale/enums/PointOfSaleCategory";
import { handleAddingProductFeeToCart, handleFeeRemoval, updateFeeQuantity } from "@unleashed-admin/features/pointOfSale/utils/MembershipFeesUtils";

const initialState: PosState = {
  selectedAccount: undefined,
  secondaryAccounts: [],
  checkInParticipants: [],
  itemsInCart: [],
  membershipAllocationData: [],
  itemsForRedemption: [],
  paymentStatus: PaymentStatus.None,
  paymentMethod: PaymentMethod.None,
  paymentType: PaymentType.None,
  totusGiftCardNumber: undefined,
  payments: [],
  taxId: undefined,
  note: undefined,
  promo: undefined,
  invoiceConfirmationCode: undefined,
  invoiceCurrencyCode: undefined,
  invoiceStatus: undefined,
  transactionDetails: {
    transactionDate: new Date().toISOString(), // TODO: This value should come from the invoice API createdDate from the response.
    checkName: undefined, // TODO: This value should come from the front-end, representing either the pager number or the customer's name so that the expo can run the food to the correct customer. Work reflected in APK-23.
  },
  requireManagerPinForManualCardEntry: undefined,
  managerCode: undefined,
  invoiceSummary: undefined,
  locationHouseAccountId: undefined,
  locationJustifiSubAccountId: undefined,
  selectedProduct: undefined,
  editProductModifierOrigin: PosTabsType.None,
  orderNameOrPagerNumber: undefined,
  charityDetails: undefined,
  tips: undefined,
  selectedInvoice: undefined,
  ticketUpgradeData: [],
  membershipAgreementSigned: undefined,
  contractSignatureData: null as CreateAcknowledgement | null,
  easterEggActive: false,
  easterEggKey: 0,
  easterEggDismissing: false,
  isBiMonthlyBilling: undefined,
};

export interface PosState {
  products?: Product[];
  selectedAccount?: AccountParticipant;
  membershipAllocationData: MembershipAllocationDataModel[];
  secondaryAccounts: AccountParticipant[];
  itemsInCart?: CartItem[];
  checkInParticipants: Account[];
  itemsForRedemption: PrePurchasedItem[];
  taxId?: string;
  promo?: Promo;
  previewApiStatus?: PreviewApiStatus;
  paymentStatus: PaymentStatus;
  paymentMethod: PaymentMethod;
  paymentType: PaymentType;
  totusGiftCardNumber?: string;
  invoiceConfirmationCode?: string;
  invoiceCurrencyCode?: string;
  invoiceStatus?: string;
  payments: Payment[];
  note?: string;
  transactionDetails: TransactionDetail;
  requireManagerPinForManualCardEntry?: boolean;
  managerCode?: string;
  invoiceSummary?: InvoiceSummary;
  locationHouseAccountId?: number;
  locationJustifiSubAccountId?: string;
  selectedProduct?: Product | CartItem | PrePurchasedItem;
  editProductModifierOrigin: PosTabsType;
  orderNameOrPagerNumber?: string;
  charityDetails?: CharityDetails;
  tips?: number;
  selectedInvoice?: AccountParticipant;
  ticketUpgradeData: TicketUpgradeDataModel[];
  membershipAgreementSigned?: boolean;
  contractSignatureData?: CreateAcknowledgement | null;
  easterEggActive: boolean;
  easterEggKey: number;
  easterEggDismissing: boolean;
  isBiMonthlyBilling?: boolean;
}

export const PosSlice = createSlice({
  name: "pos",
  initialState,
  reducers: {
    activateEasterEgg: (state) => {
      state.easterEggKey += 1;
      state.easterEggActive = true;
      state.easterEggDismissing = false;
    },
    dismissEasterEggStart: (state) => {
      state.easterEggDismissing = true;
    },
    dismissEasterEggComplete: (state) => {
      state.easterEggActive = false;
      state.easterEggDismissing = false;
    },
    setIsBiMonthlyBilling: (state, action: PayloadAction<boolean | undefined>) => {
      state.isBiMonthlyBilling = action.payload;

      if (!action.payload) {
        state.itemsInCart = (state.itemsInCart ?? []).map(
          (cartItem: CartItem) => {
            if (isMembership(cartItem)) {
              const catalogProduct = state.products?.find(
                (p) =>
                  getProductIdOrTicketIdFromProduct(p) ===
                  getProductIdOrTicketIdFromProduct(cartItem),
              );
              if (catalogProduct?.price !== undefined) {
                return { ...cartItem, price: catalogProduct.price };
              }
            }
            return cartItem;
          },
        );
      }
    },
    setSelectedAccount: (
      state: PosState,
      action: PayloadAction<AccountParticipant>
    ) => {
      state.selectedAccount = action.payload;
      updateStateWithSecondaryAccounts(state);
    },

    setMembershipAgreementSigned: (
      state: PosState,
      action: PayloadAction<boolean>
    ) => {
      state.membershipAgreementSigned = action.payload;
    },

    setContractSignatureData: (state, action) => {
      state.contractSignatureData = action.payload;
    },

    clearContractSignatureData: (state) => {
      state.contractSignatureData = null;
    },
    setSecondaryAccount: (
      state: PosState,
      action: PayloadAction<AccountParticipant>
    ) => {
      state.secondaryAccounts = state.secondaryAccounts ?? [];

      if (
        !state.secondaryAccounts.some(
          (account: AccountParticipant) =>
            account.accountId === action.payload.accountId
        )
      ) {
        state.secondaryAccounts.push(action.payload);
      }
    },

    assignMembership: (
      state,
      action: PayloadAction<{ item: BaseProduct; participantId: number }>
    ) => {
      assignMembershipToParticipant(
        state,
        action.payload.participantId,
        action.payload.item
      );
    },

    unassignMembership: (state, action: PayloadAction<number>) => {
      unAssignMembershipFromParticipant(state, action.payload);
    },

    unassignAllParticipantsFromMembershipPOS: (
      state,
      action: PayloadAction<CartItem>
    ) => {
      unassignAllParticipantsFromMembership(
        state.selectedAccount,
        action.payload
      );
    },

    unnassignAllMemberships: (state) => {
      unAssignAllMemberships(state);
    },

    removeSecondaryAccount: (
      state: PosState,
      action: PayloadAction<AccountParticipant>
    ) => {
      state.secondaryAccounts = state.secondaryAccounts.filter(
        (account: AccountParticipant) =>
          account.accountId !== action.payload.accountId
      );

      updateStateWithSecondaryAccounts(state);
    },

    updatePrePurchasedProducts(state, action: PayloadAction<PrePurchasedItem>) {
      updateStateForPrePurchasedItem(state, action.payload);
    },

    addProductToCart(state, action: PayloadAction<{ cartItem: CartItem }>) {
      const { cartItem } = action.payload;

      state.itemsInCart = state.itemsInCart ?? [];

      const existingProduct = findSimilarProduct(
        state?.itemsInCart || [],
        cartItem
      ) as CartItem;

      //#req can only have on booking in cart item - overwrite with latest added
      if ((existingProduct?.bookingId ?? false) && !!cartItem.bookingId) {
        const filteredItems = state.itemsInCart.filter(
          (r) => r !== existingProduct
        );
        filteredItems.push(cartItem);
        state.itemsInCart = [...filteredItems];
        return;
      }

      //membership already in cart means the fee must be in cart already as well
      if ( isMembership(cartItem) && (cartItem.itemFee || cartItem.productFee) && existingProduct && cartItem.quantity !== 0 ) {
        const feeItem = cartItem.itemFee;
        const productFee = cartItem.productFee;

        if (feeItem) {
          const feeCartItem:CartItem = {name: feeItem.name, price: feeItem.amount, itemFeeId: feeItem.id, type: PointOfSaleCategory.Fee, color:"", isForKds:false, quantity:1, isItemFee:true, isFromCheckInTab:true } as CartItem;
          const existingFeeItem = findSimilarProduct(state.itemsInCart,feeCartItem) as CartItem;
          if (existingFeeItem) {
            existingFeeItem.quantity = cartItem.quantity;
          }
        }

        if (productFee) {
          handleAddingProductFeeToCart(state, productFee );
        }
        existingProduct.quantity = cartItem.quantity;

      } else if ( isMembership(cartItem) && (cartItem.itemFee || cartItem.productFee) && cartItem.quantity !== 0) {
        const feeItem = cartItem.itemFee;
        const productFee = cartItem.productFee;
        if (feeItem) {
          const feeCartItem = {name: feeItem.name, price: feeItem.amount, itemFeeId: feeItem.id, type: PointOfSaleCategory.Fee, color:"", isForKds:false, quantity:1, isItemFee:true, isFromCheckInTab:true} as CartItem;
          const existingFeeItem = findSimilarProduct(state.itemsInCart, feeCartItem) as CartItem;
          if (existingFeeItem) {
            existingFeeItem.quantity++;
          } else {
            state.itemsInCart.push(feeCartItem);
          }
        }
        if (productFee) {
          handleAddingProductFeeToCart(state, productFee);
        }

        state.itemsInCart.push(cartItem);
      } else if (existingProduct && cartItem.quantity !== 0) {
        existingProduct.quantity = cartItem.quantity;
        existingProduct.quantityFromCheckIn =
          (existingProduct.productOptions?.length ?? 0) > 0
            ? 0
            : cartItem.quantityFromCheckIn;
      } else if (cartItem.quantity !== 0) {
        state.itemsInCart.push(cartItem);
      }
    },

    removeProductFromCart(
      state,
      action: PayloadAction<{ cartItem: CartItem }>
    ) {
      const { cartItem } = action.payload;
      state.itemsInCart = state.itemsInCart ?? [];

      const existingProduct = findSimilarProduct(
        state.itemsInCart,
        cartItem
      ) as CartItem;

      if (!existingProduct) return;

      const isQuantityUpdate = cartItem.quantity !== 0;

      if (isQuantityUpdate) {
        // Update quantity
        updateFeeQuantity(state, cartItem);
        updateProductQuantity(existingProduct, cartItem);
        handleUpgradedTicketRemoval(state, existingProduct, true);
      } else {
        // Remove item completely
        if ( isMembership(existingProduct) || existingProduct.type == PointOfSaleCategory.Fee ) {
          handleFeeRemoval(state, cartItem);
          return;
        }
        removeItemFromCart(state, existingProduct);
        handleUpgradedTicketRemoval(state, existingProduct, false);
      }
    },

    removeProductModifierFromCart(
      state,
      action: PayloadAction<{
        cartItem: CartItem;
        productModifierId: number;
        productId: number;
      }>
    ) {
      const { cartItem, productModifierId, productId } = action.payload;

      if (cartItem.isCombo) {
        removeOptionalModifierFromComboCartItem(
          state,
          cartItem,
          productModifierId,
          productId
        );
      } else {
        if (isPaidRedeemItem(cartItem)) {
          removeOptionalModifierFromRedeemingItem(
            state,
            cartItem,
            productModifierId
          );
        } else {
          removeOptionalModifierFromCartItem(
            state,
            cartItem,
            productModifierId
          );
        }
      }
    },

    addProducts: (state, action: PayloadAction<Product[]>) => {
      state.products = action.payload;
    },

    addTaxId: (state, action: PayloadAction<string>) => {
      state.taxId = action.payload;
      return state;
    },

    removeTaxId: (state) => {
      state.taxId = undefined;
    },

    addPromoCode: (state, action: PayloadAction<Promo>) => {
      state.promo = action.payload;
      return state;
    },

    removePromoCode: (state) => {
      state.promo = undefined;
    },

    setPreviewApiStatus: (state, action: PayloadAction<PreviewApiStatus>) => {
      state.previewApiStatus = action.payload;
    },

    setPaymentStatus: (state, action: PayloadAction<PaymentStatus>) => {
      state.paymentStatus = action.payload;
    },

    setPaymentMethod: (state, action: PayloadAction<PaymentMethod>) => {
      state.paymentMethod = action.payload;
    },

    setPaymentType: (state, action: PayloadAction<PaymentType>) => {
      state.paymentType = action.payload;
    },

    setTotusGiftCardNumber: (state, action: PayloadAction<string | undefined>) => {
      state.totusGiftCardNumber = action.payload;
    },

    addPayment: (state, action: PayloadAction<Payment>) => {
      state.payments.push(action.payload);
    },

    updatePayment: (state, action: PayloadAction<Payment>) => {
      state.payments.forEach((payment: Payment) => {
        if (payment.paymentSequenceId === action.payload.paymentSequenceId) {
          payment.lastFour = action.payload.lastFour;
          payment.paymentStatus = action.payload.paymentStatus;

          if (action.payload.cardBrand)
            payment.cardBrand = action.payload.cardBrand;

          if (action.payload.tenderAmount)
            payment.tenderAmount = action.payload.tenderAmount;

          if (action.payload.cardReaderId)
            payment.cardReaderId = action.payload.cardReaderId;

          if (action.payload.checkoutId)
            payment.checkoutId = action.payload.checkoutId;

          if (action.payload.checkNumber)
            payment.checkNumber = action.payload.checkNumber;

          if (action.payload.idempotencyKey)
            payment.idempotencyKey = action.payload.idempotencyKey;

          if (action.payload.newBalance)
            payment.newBalance = action.payload.newBalance;
        }
      });
    },

    updatePaymentAmount: (state, action: PayloadAction<Payment>) => {
      state.payments.forEach((payment: Payment) => {
        if (payment.paymentSequenceId === action.payload.paymentSequenceId) {
          payment.paymentAmount = action.payload.paymentAmount;
          payment.tenderAmount = action.payload.tenderAmount;
          // [CORE-547] A changed amount is a NEW payment intent — rotate this entry's key so the
          // backend treats it as such (stable keys only identify an unchanged intent).
          payment.idempotencyKey = uuidv4();
        }
      });
    },

    resetPayments: (state) => {
      state.payments = [];
      state.paymentStatus = PaymentStatus.None;
      state.paymentMethod = PaymentMethod.None;
      state.paymentType = PaymentType.None;
      state.totusGiftCardNumber = undefined;
      state.invoiceSummary = undefined;
      state.invoiceConfirmationCode = undefined;
      state.invoiceCurrencyCode = undefined;
      state.invoiceStatus = undefined;
    },

    resetPaymentMethodAndState: (state) => {
      // Retain original order while filtering only Success and Failed payments
      state.payments = state.payments.filter(
        p => p.paymentStatus === PaymentStatus.Success || p.paymentStatus === PaymentStatus.Failed
      );
      state.paymentStatus = PaymentStatus.Initiated;
      state.paymentMethod = PaymentMethod.None;
      state.paymentType = PaymentType.None;
      state.totusGiftCardNumber = undefined;
    },

    resetPosState: (state) => {
      return {
        ...initialState,
        requireManagerPinForManualCardEntry:
          state.requireManagerPinForManualCardEntry,
        locationHouseAccountId: state.locationHouseAccountId,
        locationJustifiSubAccountId: state.locationJustifiSubAccountId,
        products: state.products,
        charityDetails: state.charityDetails
          ? {
            ...state.charityDetails,
            status: CharityStatus.None,
            amount: undefined,
            type: CharityType.None,
          }
          : undefined,
      };
    },

    resetPosStateOnLocationChange: () => {
      return initialState;
    },

    addNote: (state, action: PayloadAction<string | undefined>) => {
      state.note = action.payload;
    },

    deleteNote: (state) => {
      state.note = undefined;
    },

    resetCart: (state) => {
      state.itemsInCart = state.itemsInCart || [];
      state.orderNameOrPagerNumber = undefined;
      state.taxId = undefined;
      state.promo = undefined;
      resetCharityData(state);
      unAssignAllMemberships(state);
      state.itemsInCart = [];
      state.selectedProduct = undefined;

      // TODO AK - Temporary fix for APK-1665 & APK-1677
      // state.selectedAccount = undefined;

      state.itemsForRedemption.forEach((item: PrePurchasedItem) => {
        handleRedeemingItemReset(state, item);
      });

      state.ticketUpgradeData = [];
    },

    removeCheckInProductsFromCart: (state) => {
      state.itemsInCart = state.itemsInCart?.filter(
        (item: CartItem) => !item.isFromCheckInTab
      );
    },

    removeQuantityFromCheckIn: (state) => {
      if (!state.itemsInCart) return;
      state.itemsInCart = state.itemsInCart
        .map((item: CartItem) => {
          if (item.quantityFromCheckIn && item.quantityFromCheckIn > 0) {
            item.quantity -= item.quantityFromCheckIn;
            item.quantityFromCheckIn = 0;
          }
          return item;
        })
        .filter((item) => item.quantity > 0);
    },

    updateStateBasedOnPurchasePreview: (
      state: PosState,
      action: PayloadAction<PurchaseViewModel>
    ) => {
      state.itemsInCart = state.itemsInCart ?? [];

      const purchasePreviewResponseViewModel = action.payload;

      state.itemsInCart = state.itemsInCart.map((cartItem: CartItem) => {
        if (isParentTicket(cartItem)) {
          const matchingPurchaseItems =
            purchasePreviewResponseViewModel.purchaseItems?.find(
              (purchaseItem: PurchaseItemViewModel) =>
                getProductIdOrTicketIdFromProduct(purchaseItem) ===
                getProductIdOrTicketIdFromProduct(cartItem)
            );

          if (matchingPurchaseItems)
            return { ...cartItem, price: matchingPurchaseItems?.pricePerUnit };
        }
        return cartItem;
      });
      
      // Update membership price from the preview response to keep pricing
      // in sync with the selected billing frequency (monthly or bi-monthly)
      state.itemsInCart = state.itemsInCart.map((cartItem: CartItem) => {
        if (isMembership(cartItem)) {
          const matchingPurchaseItem =
            purchasePreviewResponseViewModel.purchaseItems?.find(
              (purchaseItem: PurchaseItemViewModel) =>
                getProductIdOrTicketIdFromProduct(purchaseItem) ===
                getProductIdOrTicketIdFromProduct(cartItem),
            );
          if (matchingPurchaseItem)
            return { ...cartItem, price: matchingPurchaseItem.pricePerUnit };
        }
        return cartItem;
      });

      // Update discounts for booking items.
      state.itemsInCart = state.itemsInCart.map((cartItem: CartItem) => {
        if (cartItem.isBookingEvent) {
          return updateProductDiscountForBooking(
            cartItem,
            purchasePreviewResponseViewModel
          );
        }
        return cartItem;
      });

      // Update discounts for non-upgraded in itemsInCart
      state.itemsInCart = state.itemsInCart.map((cartItem: CartItem) => {
        if (
          !cartItem.isUpgradedCartTicket &&
          !cartItem.isCombo &&
          !cartItem.isBookingEvent &&
          !cartItem.isBundle
        ) {
          return updateProductDiscountForItemInCart(
            cartItem,
            purchasePreviewResponseViewModel
          );
        }
        return cartItem;
      });

      // Update discounts for upgraded-tickets in itemsInCart
      handleUpgradeTicketsDiscounts(state, purchasePreviewResponseViewModel);

      // Update discounts for combo-items in itemsInCart
      state.itemsInCart = state.itemsInCart.map((cartItem: CartItem) => {
        if (cartItem.isCombo) {
          return updateProductDiscountForCombo(
            cartItem,
            purchasePreviewResponseViewModel
          );
        }
        return cartItem;
      });

      // Update discounts for bundle-items in itemsInCart
      state.itemsInCart = state.itemsInCart.map((cartItem: CartItem) => {
        if (cartItem.isBundle) {
          return updateProductDiscountForBundle(
            cartItem,
            purchasePreviewResponseViewModel
          );
        }
        return cartItem;
      });

      // Update discounts for redeeming-items in itemsForRedemption
      handleRedemptionDiscounts(state, purchasePreviewResponseViewModel);

      state.promo = {
        ...state.promo,
        promoDiscount: purchasePreviewResponseViewModel.promoDiscount,
        couponError: purchasePreviewResponseViewModel.couponError,
      };
    },

    setManagerCode: (state, action: PayloadAction<string>) => {
      state.managerCode = action.payload;
    },

    resetManagerCode: (state) => {
      state.managerCode = undefined;
    },

    setInvoiceSummary: (state, action: PayloadAction<PurchaseViewModel>) => {
      const subtotal = action.payload.subtotal;
      const paid = action.payload.payments?.reduce((acc: number, payment) => acc + (payment.subtotalAmount ?? 0), 0);
      const tax = action.payload.tax;
      const total = action.payload.total;
      const promoDiscount = action.payload.promoDiscount;
      const itemDiscount = action.payload.itemDiscount;
      const memberDiscount = action.payload.memberDiscount;
      const totalDiscount = action.payload.totalDiscount;
      const fees = action.payload.fees;
      const tips = action.payload.tips;
      const charityAmount = action.payload.charityDetails?.charityAmount;

      state.invoiceSummary = {
        subtotal: subtotal ?? 0,
        subtotalPaid: paid ?? 0,
        tax: tax ?? 0,
        fees: fees ?? 0,
        tips: tips ?? 0,
        total: total ?? 0,
        promoDiscount: promoDiscount ?? 0,
        memberDiscount: memberDiscount ?? 0,
        itemDiscount: itemDiscount ?? 0,
        totalDiscount: totalDiscount ?? 0,
        charity: charityAmount ?? 0,
      };

      if (state.charityDetails) state.charityDetails.amount = charityAmount;
    },

    setInvoiceConfirmationCode: (state, action: PayloadAction<string>) => {
      state.invoiceConfirmationCode = action.payload;
    },

    setInvoiceCurrencyCode: (state, action: PayloadAction<string>) => {
      state.invoiceCurrencyCode = action.payload;
    },

    setInvoiceStatus: (state, action: PayloadAction<InvoiceStatus>) => {
      state.invoiceStatus = action.payload;
    },

    setRequireManagerPinForManualCardEntry: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.requireManagerPinForManualCardEntry = action.payload;
    },

    setLocationHouseAccountId: (state, action: PayloadAction<number>) => {
      state.locationHouseAccountId = action.payload;
    },

    updateTransactionDate: (state) => {
      state.transactionDetails.transactionDate = new Date().toISOString();
    },

    setLocationJustifiSubAccountId: (state, action: PayloadAction<string>) => {
      state.locationJustifiSubAccountId = action.payload;
    },

    setOrderNameOrPagerNumber: (state, action: PayloadAction<string>) => {
      state.orderNameOrPagerNumber = action.payload;
    },

    resetOrderNameOrPagerNumber: (state) => {
      state.orderNameOrPagerNumber = undefined;
    },

    setSelectedProduct: (
      state: PosState,
      action: PayloadAction<Product | undefined>
    ) => {
      state.selectedProduct = action.payload;
    },

    updateProductOptionsForCartItem: (
      state: PosState,
      action: PayloadAction<{
        selectedCartItem: CartItem;
        updatedCartItem: CartItem;
      }>
    ) => {
      const { selectedCartItem, updatedCartItem } = action.payload;

      if (isSameProduct(selectedCartItem, updatedCartItem)) return;

      // Ensure `itemsInCart` is initialized
      state.itemsInCart = state.itemsInCart ?? [];

      const existingProduct = findSimilarProduct(
        state.itemsInCart,
        selectedCartItem
      ) as CartItem;

      if (!existingProduct) return;

      // Find if a product with the same updated modifiers already exists
      const existingProductWithUpdatedModifiers = findSimilarProduct(
        state.itemsInCart,
        updatedCartItem
      ) as CartItem;

      if (existingProductWithUpdatedModifiers) {
        // Remove the old product and update the quantity of the existing one
        state.itemsInCart = state.itemsInCart.filter(
          (item: CartItem) => item !== existingProduct
        );
        existingProductWithUpdatedModifiers.quantity +=
          updatedCartItem.quantity;
      } else {
        // Update product options and quantity for the existing product
        existingProduct.productOptions = updatedCartItem.productOptions;
      }
    },

    updateProductOptionsForForRedemptionItem: (
      state: PosState,
      action: PayloadAction<{
        selectedRedemptionItem: PrePurchasedItem;
        updatedProductOptions: ProductOptionViewModel[];
      }>
    ) => {
      const { selectedRedemptionItem, updatedProductOptions } = action.payload;

      const existingProduct = selectedRedemptionItem.uuid
        ? state.itemsForRedemption.find(
          (item: PrePurchasedItem) =>
            item.uuid === selectedRedemptionItem.uuid
        )
        : (findSimilarProduct(
          state.itemsForRedemption,
          selectedRedemptionItem
        ) as PrePurchasedItem);

      if (!existingProduct) return;

      if (selectedRedemptionItem.uuid) {
        existingProduct.productOptions = updatedProductOptions;
      } else {
        state.itemsForRedemption = state.itemsForRedemption.map(
          (item: PrePurchasedItem) => {
            if (isSameProduct(item, selectedRedemptionItem)) {
              item.productOptions = updatedProductOptions;
            }
            return item;
          }
        );
      }
    },

    setEditProductModifierOrigin: (
      state,
      action: PayloadAction<PosTabsType>
    ) => {
      state.editProductModifierOrigin = action.payload;
    },

    updateComboConfigForCartItem: (
      state: PosState,
      action: PayloadAction<{
        selectedCartItem: CartItem;
        updatedCartItem: CartItem;
      }>
    ) => {
      const { selectedCartItem, updatedCartItem } = action.payload;

      if (isSameProduct(selectedCartItem, updatedCartItem)) return;

      // Ensure `itemsInCart` is initialized
      state.itemsInCart = state.itemsInCart ?? [];

      const existingProduct = findSimilarProduct(
        state.itemsInCart,
        selectedCartItem
      ) as CartItem;

      if (!existingProduct) return;

      // Find if a product with the same updated modifiers already exists
      const existingProductWithUpdatedModifiers = findSimilarProduct(
        state.itemsInCart,
        updatedCartItem
      ) as CartItem;

      if (existingProductWithUpdatedModifiers) {
        // Remove the old product and update the quantity of the existing one
        state.itemsInCart = state.itemsInCart.filter(
          (item: CartItem) => item !== existingProduct
        );
        existingProductWithUpdatedModifiers.quantity +=
          updatedCartItem.quantity;
      } else {
        // Update product options and quantity for the existing product
        existingProduct.comboConfigs = updatedCartItem.comboConfigs;
      }
    },

    setTips: (state, action: PayloadAction<number>) => {
      state.tips = action.payload;
    },

    resetTips: (state) => {
      state.tips = initialState.tips;
    },

    setCharityDetails: (state, action: PayloadAction<CharityDetails>) => {
      state.charityDetails = action.payload;
    },

    resetCharityDetails: (
      state,
      action: PayloadAction<boolean | undefined>
    ) => {
      resetCharityData(state, action.payload);
    },

    updateCheckInParticipants: (
      state,
      action: PayloadAction<{ isChecked: boolean; participant: Participant }>
    ) => {
      const { isChecked, participant } = action.payload;

      const existingParticipantIndex = state.checkInParticipants.findIndex(
        (p) => p.id === participant.id
      );

      if (isChecked) {
        if (existingParticipantIndex === -1) {
          state.checkInParticipants.push(participant);
        }
      } else {
        if (existingParticipantIndex !== -1) {
          state.checkInParticipants.splice(existingParticipantIndex, 1);

          if (participant && participant.id) {
            unAssignMembershipFromParticipant(state, participant.id, true);
          }
        }
      }
    },

    updateStateForMembershipsAllocated: (state) => {
      updateStateOnCheckIn(state);
    },

    setSelectedInvoice: (
      state: PosState,
      action: PayloadAction<AccountParticipant>
    ) => {
      state.selectedInvoice = action.payload;
    },

    updateStateOnTicketUpgrade: (
      state,
      action: PayloadAction<TicketUpgradeDataModel[]>
    ) => {
      applyTicketUpgradeChanges(state, action.payload);
    },
    updateCartItemVariablePrice: (
      state,
      action: PayloadAction<{ productId: number; variablePrice: number }>
    ) => {
      state.itemsInCart = state.itemsInCart ?? [];
      const item = state.itemsInCart.find(
        (item) => item.productId === action.payload.productId
      );
      if (item) {
        item.variablePrice = action.payload.variablePrice;
      }
    },
    updateCartItemMembershipStartDate: (
      state,
      action: PayloadAction<{ productId: number; membershipStartDate: string }>
    ) => {
      state.itemsInCart = state.itemsInCart ?? [];
      const item = state.itemsInCart.find(
        (item) => item.productId === action.payload.productId
      );

      if (item && isMembership(item)) {
        item.membershipBillingStartDate = action.payload.membershipStartDate;
      }
    },
  },
});

export const {
  setSelectedAccount,
  setSecondaryAccount,
  removeSecondaryAccount,
  addProductToCart,
  removeProductFromCart,
  removeProductModifierFromCart,
  updatePrePurchasedProducts,
  setPreviewApiStatus,
  setPaymentStatus,
  addTaxId,
  addProducts,
  removeTaxId,
  setPaymentMethod,
  setPaymentType,
  updateStateForMembershipsAllocated,
  addPayment,
  updatePayment,
  resetPayments,
  resetPosState,
  resetPosStateOnLocationChange,
  addNote,
  deleteNote,
  addPromoCode,
  removePromoCode,
  resetCart,
  removeCheckInProductsFromCart,
  updateStateBasedOnPurchasePreview,
  setManagerCode,
  resetManagerCode,
  setInvoiceSummary,
  setInvoiceConfirmationCode,
  setInvoiceCurrencyCode,
  setInvoiceStatus,
  setRequireManagerPinForManualCardEntry,
  setTotusGiftCardNumber,
  removeQuantityFromCheckIn,
  setLocationHouseAccountId,
  updateTransactionDate,
  setLocationJustifiSubAccountId,
  setOrderNameOrPagerNumber,
  resetOrderNameOrPagerNumber,
  setSelectedProduct,
  updateProductOptionsForCartItem,
  updateProductOptionsForForRedemptionItem,
  setEditProductModifierOrigin,
  updateComboConfigForCartItem,
  setCharityDetails,
  resetCharityDetails,
  updateCheckInParticipants,
  setTips,
  resetTips,
  setSelectedInvoice,
  updateStateOnTicketUpgrade,
  updateCartItemVariablePrice,
  updateCartItemMembershipStartDate,
  assignMembership,
  unassignMembership,
  unnassignAllMemberships,
  setMembershipAgreementSigned,
  setContractSignatureData,
  clearContractSignatureData,
  activateEasterEgg,
  dismissEasterEggStart,
  dismissEasterEggComplete,
  unassignAllParticipantsFromMembershipPOS,
  updatePaymentAmount,
  resetPaymentMethodAndState,
  setIsBiMonthlyBilling,
} = PosSlice.actions;
